function escapeHtml(value) {
  return String(value ?? '').replaceAll('&', '&amp;').replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#039;');
}

export function podEvidenceOnly(items) {
  if (!Array.isArray(items)) return [];
  return items.filter((item) => item && typeof item === 'object' && item.kind === 'pod');
}

export function shouldUseDemoPodEvidence({
  containerId = '', podCount = 0, hasPod = false,
} = {}) {
  return !hasPod
    && /^km_demo_container_\d+$/i.test(String(containerId || '').trim())
    && Number(podCount) > 0;
}

export function dashboardDemoPodEvidence({
  containerNo = 'Container', count = 0, assetBaseUrl = '/assets',
} = {}) {
  const total = Math.min(5, Math.max(0, Number.isSafeInteger(Number(count)) ? Number(count) : 0));
  const base = String(assetBaseUrl || '/assets').replace(/\/+$/, '');
  const notes = [
    'Container and seal verified at delivery.',
    'Warehouse delivery position and doors documented.',
    'Final POD condition recorded before task completion.',
  ];
  return Array.from({ length: total }, (_, index) => ({
    id: `demo-pod-${index + 1}`,
    kind: 'pod',
    objectUrl: `${base}/demo-pod-${(index % 3) + 1}.svg`,
    created_at: new Date(Date.UTC(2026, 6, 17, 16 + index, 15)).toISOString(),
    dispatcher_status: 'delivered_to_warehouse',
    note: `${containerNo} — ${notes[index % notes.length]}`,
  }));
}

function evidenceDate(value) {
  let date = null;
  if (value instanceof Date) date = value;
  else if (value && typeof value.toDate === 'function') date = value.toDate();
  else if (Number.isFinite(Number(value))) date = new Date(Number(value));
  else if (typeof value === 'string' && value.trim()) date = new Date(value);
  return date instanceof Date && Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat('en-US', {
      // 24-hour everywhere (Alan, 2026-08-11).
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).format(date)
    : 'Not provided';
}

function evidenceStatus(value) {
  const normalized = String(value || '').trim();
  if (!normalized) return '';
  return normalized.split('_').map((part) => (
    part ? part[0].toUpperCase() + part.slice(1).toLowerCase() : ''
  )).join(' ');
}

function evidenceAspectRatio(value) {
  return String(value || '').replace(/\s+/g, '') === '16/9' ? '16 / 9' : '4 / 3';
}

function photoMarkup(item, index, containerNo) {
  const note = String(item.note || '').trim() || 'No note';
  const status = evidenceStatus(item.status || item.dispatcher_status || item.operational_status);
  const aspectRatio = evidenceAspectRatio(item.aspectRatio || item.aspect_ratio);
  const hasImage = Boolean(String(item.objectUrl || '').trim());
  const preview = hasImage
    ? `<img src="${escapeHtml(item.objectUrl)}" alt="POD photo ${index + 1}">`
    : '<div class="dashboard-pod-viewer__unavailable">Preview unavailable</div>';
  const interactionAttributes = hasImage
    ? `tabindex="0" aria-label="POD photo ${index + 1}; hover, focus, or select to view details"`
    : `aria-label="POD photo ${index + 1}; preview unavailable"`;
  return `
    <article class="dashboard-pod-viewer__photo dashboard-pod-viewer__photo--${hasImage ? 'has-image' : 'unavailable'}" style="--pod-photo-index:${index};--pod-aspect-ratio:${aspectRatio}" ${interactionAttributes}>
      <div class="dashboard-pod-viewer__photo-inner">
        <div class="dashboard-pod-viewer__front">
          <div class="dashboard-pod-viewer__image">${preview}</div>
        </div>
        <div class="dashboard-pod-viewer__back">
          <strong>${escapeHtml(containerNo || 'Container')}</strong>
          <dl>
            <div class="dashboard-pod-viewer__back-note"><dt>Note</dt><dd>${escapeHtml(note)}</dd></div>
            <div><dt>Time</dt><dd>${escapeHtml(evidenceDate(item.created_at))}</dd></div>
            ${status ? `<div><dt>Status</dt><dd>${escapeHtml(status)}</dd></div>` : ''}
          </dl>
        </div>
      </div>
    </article>`;
}

export function dashboardPodViewerMarkup({
  containerNo = '', items = [], loading = false, error = false,
} = {}) {
  const safeItems = podEvidenceOnly(items);
  let content = '';
  if (loading) {
    content = '<div class="dashboard-pod-viewer__status" role="status">Loading POD photos&hellip;</div>';
  } else if (error) {
    content = '<div class="dashboard-pod-viewer__status is-error" role="alert">POD photos could not be loaded.</div>';
  } else if (!safeItems.length) {
    content = '<div class="dashboard-pod-viewer__status">No POD photos are available yet.</div>';
  } else {
    content = `<div class="dashboard-pod-viewer__photos">${safeItems.map((item, index) => photoMarkup(item, index, containerNo)).join('')}</div>`;
  }
  return `
    <section class="dashboard-pod-viewer__surface">
      ${content}
    </section>`;
}

export function createDashboardPodViewer({
  documentRef = document,
  loadAssets,
} = {}) {
  if (!documentRef?.body || typeof documentRef.createElement !== 'function'
    || typeof loadAssets !== 'function') {
    throw new TypeError('Dashboard POD viewer dependencies are invalid.');
  }

  let viewer = null;
  let trigger = null;
  let assetCleanup = () => {};
  let generation = 0;

  function removeListeners() {
    documentRef.removeEventListener('keydown', onKeydown, true);
  }

  function close({ restoreFocus = true } = {}) {
    generation += 1;
    const focusTarget = trigger;
    trigger = null;
    removeListeners();
    viewer?.remove();
    viewer = null;
    assetCleanup();
    assetCleanup = () => {};
    if (restoreFocus) focusTarget?.focus?.({ preventScroll: true });
  }

  function onKeydown(event) {
    if (event.key === 'Escape') close();
  }

  async function open({ containerId, containerNo, podCount = 0, anchor = null } = {}) {
    if (typeof containerId !== 'string' || !containerId.trim()) return false;
    close({ restoreFocus: false });
    const token = generation;
    trigger = anchor;
    viewer = documentRef.createElement('div');
    viewer.className = 'dashboard-pod-viewer';
    viewer.dataset.podViewerBackdrop = '';
    viewer.setAttribute('role', 'dialog');
    viewer.setAttribute('aria-modal', 'false');
    viewer.setAttribute('aria-label', `POD photos for ${containerNo || 'Container'}`);
    viewer.tabIndex = -1;
    viewer.innerHTML = dashboardPodViewerMarkup({ containerNo, loading: true });
    viewer.addEventListener('click', (event) => {
      if (event.target === viewer) {
        close();
        return;
      }
      const card = event.target.closest?.('.dashboard-pod-viewer__photo');
      if (card?.classList.contains('dashboard-pod-viewer__photo--has-image')) {
        card.classList.toggle('is-flipped');
      }
    });
    documentRef.body.append(viewer);
    documentRef.addEventListener('keydown', onKeydown, true);
    viewer.focus?.({ preventScroll: true });

    try {
      const result = await loadAssets(containerId, { containerNo, podCount });
      if (token !== generation || !viewer) {
        result?.cleanup?.();
        return false;
      }
      assetCleanup = typeof result?.cleanup === 'function' ? result.cleanup : () => {};
      viewer.innerHTML = dashboardPodViewerMarkup({
        containerNo,
        items: podEvidenceOnly(result?.items),
      });
      viewer.querySelectorAll('.dashboard-pod-viewer__image img').forEach((image) => {
        image.addEventListener('load', () => {
          const ratio = image.naturalWidth / Math.max(1, image.naturalHeight);
          image.closest('.dashboard-pod-viewer__photo')?.style.setProperty(
            '--pod-aspect-ratio', ratio > 1.55 ? '16 / 9' : '4 / 3',
          );
        }, { once: true });
      });
      return true;
    } catch {
      if (token === generation && viewer) {
        viewer.innerHTML = dashboardPodViewerMarkup({ containerNo, error: true });
      }
      return false;
    }
  }

  return Object.freeze({
    open,
    close,
    isOpen: () => viewer !== null,
    destroy: () => close({ restoreFocus: false }),
  });
}
